import { useState, useEffect } from "react";
import { Trash2 } from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuthContext";

const STATUS_COLORS = {
  new: "bg-emerald-50 text-emerald-700",
  reviewing: "bg-blue-50 text-blue-700",
  responded: "bg-purple-50 text-purple-700",
  closed: "bg-zinc-100 text-zinc-500",
};

export default function InquiriesPage() {
  const { adminAxios } = useAdminAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [noteInput, setNoteInput] = useState("");

  const load = () => adminAxios.get("/admin/inquiries").then(({ data }) => setInquiries(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await adminAxios.put(`/admin/inquiries/${id}`, { status });
    load();
    if (selected?.id === id) setSelected(s => ({ ...s, status }));
  };

  const saveNote = async () => {
    if (!selected) return;
    await adminAxios.put(`/admin/inquiries/${selected.id}`, { notes: noteInput });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    await adminAxios.delete(`/admin/inquiries/${id}`);
    if (selected?.id === id) setSelected(null);
    load();
  };

  return (
    <div className="p-8 min-h-screen bg-zinc-50">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-zinc-900 tracking-tight">Inquiries</h1>
        <p className="text-sm text-zinc-500 mt-0.5">{inquiries.filter(i => i.status === "new").length} new, {inquiries.length} total</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-zinc-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100">
                {["Name", "Property", "Phone", "Status", "Date", ""].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="px-4 py-6 text-zinc-400">Loading...</td></tr>
              : inquiries.length === 0 ? <tr><td colSpan={6} className="px-4 py-10 text-zinc-400 text-center">No inquiries yet.</td></tr>
              : inquiries.map(inq => (
                <tr key={inq.id} onClick={() => { setSelected(inq); setNoteInput(inq.notes || ""); }} className={`border-b border-zinc-50 cursor-pointer transition-colors ${selected?.id === inq.id ? "bg-red-50" : "hover:bg-zinc-50"}`} data-testid={`inquiry-row-${inq.id}`}>
                  <td className="px-4 py-3 font-medium text-zinc-900">{inq.name}</td>
                  <td className="px-4 py-3 text-zinc-600 text-xs">{inq.property_name || "—"}</td>
                  <td className="px-4 py-3 text-zinc-500">{inq.phone}</td>
                  <td className="px-4 py-3">
                    <select value={inq.status} onChange={e => { e.stopPropagation(); updateStatus(inq.id, e.target.value); }} onClick={e => e.stopPropagation()} className={`text-xs px-2 py-0.5 font-medium border-0 bg-transparent focus:outline-none cursor-pointer ${STATUS_COLORS[inq.status] || ""}`}>
                      {["new", "reviewing", "responded", "closed"].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 text-xs">{new Date(inq.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3"><button onClick={e => { e.stopPropagation(); del(inq.id); }} className="text-zinc-300 hover:text-[#C8102E] transition-colors"><Trash2 size={13} strokeWidth={1.5} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-zinc-200 p-5">
          {selected ? (
            <div className="space-y-4">
              <div><p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">From</p><p className="font-medium text-zinc-900">{selected.name}</p></div>
              <div><p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Phone</p><p className="text-zinc-700">{selected.phone}</p></div>
              <div><p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Property</p><p className="text-zinc-700">{selected.property_name || "—"}</p></div>
              {selected.message && <div><p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Message</p><p className="text-zinc-600 text-sm leading-relaxed">{selected.message}</p></div>}
              <div><p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Date</p><p className="text-zinc-500 text-sm">{new Date(selected.created_at).toLocaleString()}</p></div>
              <div>
                <p className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Notes</p>
                <textarea value={noteInput} onChange={e => setNoteInput(e.target.value)} rows={3} className="w-full border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:border-zinc-400 resize-none" placeholder="Add notes..." />
                <button onClick={saveNote} className="mt-2 text-xs bg-zinc-800 text-white px-4 py-1.5 hover:bg-zinc-700 transition-colors">Save Note</button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-zinc-300 text-sm">Select an inquiry to view details</div>
          )}
        </div>
      </div>
    </div>
  );
}
